import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { organizationService } from '../../services/organizationService';
import { Organization } from '../../types/organization';
import { useOrganizations } from '../../hooks/useOrganizations';
import toast from 'react-hot-toast';
import { BuildingOfficeIcon, MapPinIcon, UserCircleIcon, CloudArrowUpIcon, XMarkIcon, UsersIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import FormTabs from '../common/FormTabs';
import OrganizationUsers from './OrganizationUsers';
import OrganizationSubscriptionTab from './OrganizationSubscriptionTab';
import { useStorage } from '../../hooks/useStorage';

import { useLanguage } from '../../contexts/LanguageContext';
import { formatTaxNumber, formatRegistrationNumber } from '../../utils/validation';

interface FormData {
  name: string;
  taxNumber: string;
  registrationNumber: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  contact: {
    name: string;
    email: string;
    phone: string;
    position: string;
  };
}

const initialFormData: FormData = {
  name: '',
  taxNumber: '',
  registrationNumber: '',
  address: {
    street: '',
    city: '',
    postalCode: '',
    country: ''
  },
  contact: {
    name: '',
    email: '',
    phone: '',
    position: ''
  }
};

interface OrganizationSettingsProps {
  organization: Organization;
  isEditing: boolean;
  onEditingChange: (editing: boolean) => void;
  onOrganizationUpdate: (org: Organization) => void;
  hideButtons?: boolean; // Modal-ban elrejtjük a gombokat
  onRequestDelete?: (org: Organization) => void;
}

export const OrganizationSettings: React.FC<OrganizationSettingsProps> = ({
  organization,
  isEditing,
  onEditingChange,
  onOrganizationUpdate,
  hideButtons = false,
  onRequestDelete
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const { uploadFile, deleteFile } = useStorage();
  const [isUploading, setIsUploading] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    setFormData({
      name: organization.name,
      taxNumber: organization.taxNumber,
      registrationNumber: organization.registrationNumber,
      address: organization.address,
      contact: organization.contact
    });
  }, [organization]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Apply formatting for tax number and registration number
    if (name === 'taxNumber') {
      const formattedValue = formatTaxNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
      return;
    } else if (name === 'registrationNumber') {
      const formattedValue = formatRegistrationNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
      return;
    }
    
    // Handle other fields normally
    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...(prev[section as keyof FormData] as Record<string, string>),
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await organizationService.updateOrganization(organization.id, formData);

      const updatedOrgData: Organization = {
        ...organization,
        ...formData,
        updatedAt: new Date()
      };

      onOrganizationUpdate(updatedOrgData);

      toast.success(typeof t('common.messages.organization.updateSuccess') === 'string' 
        ? t('common.messages.organization.updateSuccess') 
        : 'Szervezet sikeresen frissítve');
      onEditingChange(false);
    } catch (error) {
      console.error('Error updating organization:', error);
      toast.error(typeof t('common.messages.organization.updateError') === 'string'
        ? t('common.messages.organization.updateError')
        : 'Hiba történt a szervezet frissítésekor');
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const path = `organizations/${organization.id}/logo`;
      const logoUrl = await uploadFile(file, path);

      const updatedOrg = {
        ...organization,
        logoUrl
      } as Organization;

      await organizationService.updateOrganization(organization.id, updatedOrg);
      onOrganizationUpdate(updatedOrg);

      toast.success(typeof t('common.messages.organization.logoUploadSuccess') === 'string'
        ? t('common.messages.organization.logoUploadSuccess')
        : 'Logó sikeresen feltöltve');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(typeof t('common.messages.organization.logoUploadError') === 'string'
        ? t('common.messages.organization.logoUploadError')
        : 'Hiba történt a logó feltöltésekor');
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!organization?.logoUrl) return;

    try {
      const path = `organizations/${organization.id}/logo`;
      await deleteFile(path);

      const updatedOrg = {
        ...organization,
        logoUrl: null
      } as Organization;

      await organizationService.updateOrganization(organization.id, updatedOrg);
      onOrganizationUpdate(updatedOrg);

      toast.success('Logo removed successfully');
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error('Failed to remove logo');
    }
  };

  type OrgTab = 'company' | 'address' | 'contact' | 'logo' | 'members' | 'subscription';
  const isSuperAdmin = (user as any)?.role === 'super_admin';
  const [activeTab, setActiveTab] = useState<OrgTab>('company');
  const tabCls = (tab: OrgTab) => (activeTab === tab ? 'space-y-4' : 'hidden');
  const tabErrors = [
    ...((!formData.name?.trim()) ? ['company'] : []),
  ];

  // Sima függvény (nem komponens), hogy gépelés közben ne mountolódjon újra az input
  const field = ({ label, name, required, type = 'text', placeholder, span }: { label: string; name: string; required?: boolean; type?: string; placeholder?: string; span?: boolean }) => {
    const value = name.includes('.') ? ((formData as any)[name.split('.')[0]]?.[name.split('.')[1]] ?? '') : ((formData as any)[name] ?? '');
    return (
      <div key={name} className={span ? 'sm:col-span-2' : ''}>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{label}{required && <span className="text-rose-500"> *</span>}</label>
        <input type={type} name={name} value={value} onChange={handleChange} placeholder={placeholder} className="w-full h-9 border rounded-lg px-3 text-sm" />
      </div>
    );
  };

  return (
    <form id="org-settings-form" onSubmit={handleSubmit} noValidate className="space-y-4">
      <FormTabs
        tabs={[
          { id: 'company', label: 'Cégadatok', icon: BuildingOfficeIcon },
          { id: 'address', label: 'Cím', icon: MapPinIcon },
          { id: 'contact', label: 'Kapcsolattartó', icon: UserCircleIcon },
          { id: 'logo', label: 'Logó', icon: CloudArrowUpIcon },
          { id: 'members', label: 'Tagok', icon: UsersIcon },
          ...(isSuperAdmin ? [{ id: 'subscription', label: 'Előfizetés & rendszer', icon: CreditCardIcon }] : [])
        ]}
        active={activeTab}
        onChange={(id) => setActiveTab(id as OrgTab)}
        errorTabs={tabErrors}
        sticky={false}
      />

      <div className="md:min-h-[360px]">
        <div data-tab="company" className={tabCls('company')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {field({ label: 'Cégnév', name: 'name', required: true, span: true })}
            {field({ label: 'Adószám', name: 'taxNumber', placeholder: '12345678-1-12' })}
            {field({ label: 'Cégjegyzékszám', name: 'registrationNumber', placeholder: '01-09-123456' })}
          </div>
          <p className="text-[11px] text-gray-400">Az adószám és cégjegyzékszám az ajánlatokon, megrendeléseken és számlákon jelenik meg.</p>
        </div>

        <div data-tab="address" className={tabCls('address')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {field({ label: 'Irányítószám', name: 'address.postalCode' })}
            {field({ label: 'Város', name: 'address.city' })}
            {field({ label: 'Utca, házszám', name: 'address.street', span: true })}
            {field({ label: 'Ország', name: 'address.country', placeholder: 'Magyarország' })}
          </div>
        </div>

        <div data-tab="contact" className={tabCls('contact')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {field({ label: 'Név', name: 'contact.name' })}
            {field({ label: 'Beosztás', name: 'contact.position' })}
            {field({ label: 'E-mail', name: 'contact.email', type: 'email' })}
            {field({ label: 'Telefon', name: 'contact.phone', type: 'tel' })}
          </div>
          <p className="text-[11px] text-gray-400">Ez az e-mail kapja az ügyfél-válaszokról szóló értesítéseket (pl. elfogadott árajánlat).</p>
        </div>

        <div data-tab="logo" className={tabCls('logo')}>
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="w-32 h-32 rounded-xl border border-dashed border-[var(--crm-border)] bg-[var(--crm-bg)] flex items-center justify-center overflow-hidden flex-shrink-0">
              {organization?.logoUrl ? <img src={organization.logoUrl} alt="Logó" className="max-w-full max-h-full object-contain p-2" /> : <span className="text-xs text-gray-400">Nincs logó</span>}
            </div>
            <div className="space-y-2 text-sm">
              <label className={`crm-btn crm-btn-secondary cursor-pointer ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                <CloudArrowUpIcon className="w-4 h-4 mr-1.5" />{isUploading ? 'Feltöltés…' : 'Logó feltöltése'}
                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={isUploading} />
              </label>
              {organization?.logoUrl && <button type="button" onClick={handleLogoDelete} className="crm-btn crm-btn-ghost text-rose-600"><XMarkIcon className="w-4 h-4 mr-1" />Eltávolítás</button>}
              <p className="text-[11px] text-gray-400">PNG vagy SVG, átlátszó háttérrel a legjobb. A logó az árajánlat PDF-en, a nyilvános ajánlat-oldalon és a foglalóoldalon jelenik meg.</p>
            </div>
          </div>
        </div>

        <div data-tab="members" className={tabCls('members')}>
          <OrganizationUsers organization={organization} onOrganizationUpdate={onOrganizationUpdate} />
        </div>

        {isSuperAdmin && (
          <div data-tab="subscription" className={tabCls('subscription')}>
            <OrganizationSubscriptionTab organization={organization} onOrganizationUpdate={onOrganizationUpdate} onRequestDelete={onRequestDelete} />
          </div>
        )}
      </div>

      {!hideButtons && (
        <div className="flex justify-end gap-2 pt-2 border-t border-[var(--crm-border)]">
          <button type="button" onClick={() => onEditingChange(false)} className="crm-btn crm-btn-ghost">Mégse</button>
          <button type="submit" className="crm-btn crm-btn-primary">Mentés</button>
        </div>
      )}
    </form>
  );
};

export default OrganizationSettings;
